import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Share2, Instagram, TrendingUp, TrendingDown } from 'lucide-react';
import { formatNumber } from '@/lib/dashboard-utils.jsx';

export const SocialReachPanel = ({ socialMetrics, socialTrendData }) => {
  const hasData = socialMetrics.twitter.followers || socialMetrics.tiktok.followers || socialMetrics.instagram.followers || socialMetrics.facebook.page_likes;

  return (
    <div className="mb-8" data-testid="social-reach-section">
      <h2 className="text-2xl font-semibold text-[#00d1ff] mb-6 border-b border-[#00d1ff]/30 pb-3">SOCIAL REACH</h2>
      {!hasData ? (
        <div className="chart-container p-12 rounded-lg text-center">
          <Share2 className="w-16 h-16 text-gray-600 mx-auto mb-4 opacity-30" />
          <p className="text-xl text-gray-400 mb-2">No social data yet</p>
          <p className="text-sm text-gray-500 mb-4">Run the setup script to populate social metrics</p>
          <div className="inline-block bg-black/50 border border-gray-700 rounded-lg px-4 py-2 text-xs text-gray-400 font-mono">
            Supabase SQL Editor &rarr; paste social_metrics_setup.sql &rarr; Run
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <PlatformCard platform="twitter" label="X (Twitter)" metrics={socialMetrics.twitter} color="#fff" borderColor="white" icon={<Share2 className="w-8 h-8 text-white" />}
              rows={[{ label: 'Posts (7d)', value: socialMetrics.twitter.posts_7d || 0 }, { label: 'Impressions', value: formatNumber(socialMetrics.twitter.impressions_7d || 0) }, { label: 'Engagement Rate', value: `${socialMetrics.twitter.engagement_rate || 0}%` }]} />
            <PlatformCard platform="tiktok" label="TikTok" metrics={socialMetrics.tiktok} color="#ff0050" borderColor="#ff0050" icon={<span className="text-2xl text-[#ff0050] font-bold">♪</span>}
              rows={[{ label: 'Videos (7d)', value: socialMetrics.tiktok.videos_7d || 0 }, { label: 'Views (7d)', value: formatNumber(socialMetrics.tiktok.views_7d || 0) }, { label: 'Avg Views/Video', value: formatNumber(socialMetrics.tiktok.avg_views_per_video || 0) }]} />
            <PlatformCard platform="instagram" label="Instagram" metrics={socialMetrics.instagram} color="#E1306C" borderColor="#E1306C" icon={<Instagram className="w-8 h-8 text-[#E1306C]" />}
              rows={[{ label: 'Posts (7d)', value: socialMetrics.instagram.posts_7d || 0 }, { label: 'Reach (7d)', value: formatNumber(socialMetrics.instagram.reach_7d || 0) }, { label: 'Story Views', value: formatNumber(socialMetrics.instagram.story_views_avg || 0) }]} />
            <PlatformCard platform="facebook" label="Facebook" metrics={socialMetrics.facebook} color="#1877F2" borderColor="#1877F2" followerKey="page_likes" followerLabel="page likes"
              icon={<span className="text-3xl text-[#1877F2] font-bold">f</span>}
              rows={[{ label: 'Posts (7d)', value: socialMetrics.facebook.posts_7d || 0 }, { label: 'Reach (7d)', value: formatNumber(socialMetrics.facebook.reach_7d || 0) }, { label: 'Engagement', value: formatNumber(socialMetrics.facebook.engagement_7d || 0) }]} />
          </div>
          {socialTrendData.length > 0 && (
            <div className="chart-container p-6 rounded-lg" data-testid="social-trend-chart">
              <h3 className="text-xl font-semibold text-[#00d1ff] mb-4">FOLLOWER GROWTH (8 WEEKS)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={socialTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                  <XAxis dataKey="week" stroke="#666" tick={{ fill: '#999' }} />
                  <YAxis stroke="#666" tick={{ fill: '#999' }} />
                  <Tooltip contentStyle={{ backgroundColor: '#000', border: '1px solid #333' }} labelStyle={{ color: '#00d1ff' }} />
                  <Legend wrapperStyle={{ color: '#999' }} />
                  <Line type="monotone" dataKey="twitter" stroke="#fff" strokeWidth={2} dot={{ fill: '#fff', r: 3 }} name="X (Twitter)" />
                  <Line type="monotone" dataKey="tiktok" stroke="#ff0050" strokeWidth={2} dot={{ fill: '#ff0050', r: 3 }} name="TikTok" />
                  <Line type="monotone" dataKey="instagram" stroke="#E1306C" strokeWidth={2} dot={{ fill: '#E1306C', r: 3 }} name="Instagram" />
                  <Line type="monotone" dataKey="facebook" stroke="#1877F2" strokeWidth={2} dot={{ fill: '#1877F2', r: 3 }} name="Facebook" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PlatformCard = ({ label, metrics, color, borderColor, icon, rows, followerKey = 'followers', followerLabel = 'followers' }) => (
  <div className="bg-black/50 border rounded-lg p-6 hover:opacity-90 transition-all relative overflow-hidden"
    style={{ borderColor: `${borderColor}33`, boxShadow: `0 0 20px ${borderColor}1a` }}
    data-testid={`social-${label.toLowerCase().replace(/[^a-z]/g, '')}-card`}>
    <div className="flex items-center justify-between mb-4">
      <div className="w-8 h-8 flex items-center justify-center">{icon}</div>
      {metrics.follower_growth > 0 && <TrendingUp className="w-5 h-5 text-[#00ff41]" />}
      {metrics.follower_growth < 0 && <TrendingDown className="w-5 h-5 text-[#ff0033]" />}
    </div>
    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-3xl font-bold mb-4" style={{ color }}>{formatNumber(metrics[followerKey] || 0)}<span className="text-sm text-gray-500 ml-2">{followerLabel}</span></p>
    <div className="space-y-2 text-sm">
      {rows.map(row => (
        <div key={row.label} className="flex justify-between">
          <span className="text-gray-400">{row.label}</span>
          <span className="font-semibold" style={{ color }}>{row.value}</span>
        </div>
      ))}
    </div>
  </div>
);
